/*!
 * Copyright © 2018-2020. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';

import { isElementNull } from '../../../../utils';
import { EMapTypeId, EOverlayContainer, IIndexableObject, ILatLng, IReplayLayout, Position } from '../../models';
import { EMapAnimationState, ILayoutState } from '../state';

export const getLayoutState = createFeatureSelector<ILayoutState>('layout');

export const getZoomToLocation = createSelector(getLayoutState, (state: ILayoutState): ILatLng => state.locationToZoom);

export const getZoomToLocationAddress = createSelector(getLayoutState, (state: ILayoutState): string | null => state.zoomToLocationAddress);

export const getMoveToLocation = createSelector(getLayoutState, (state: ILayoutState): ILatLng => state.locationToMove);

export const getMoveToLocationWithOffset = createSelector(getLayoutState, (state: ILayoutState): ILatLng => state.locationToMoveWithOffset);

export const getZoomToMarkerId = createSelector(getLayoutState, (state: ILayoutState): number => state.locationToMarkerId);

export const getZoomToMarker = createSelector(getLayoutState, (state: ILayoutState): ILatLng => state.locationToMarker);

export const getMoveToMarkerId = createSelector(getLayoutState, (state: ILayoutState): number => state.locationToMoveMarkerId);

export const getMoveToMarker = createSelector(getLayoutState, (state: ILayoutState): ILatLng => state.locationToMoveMarker);

export const getIsMapFitted = createSelector(getLayoutState, (state: ILayoutState): boolean => state.isMapFitted);

export const getTriggerFitToMap = createSelector(getLayoutState, (state: ILayoutState): Object => state.triggerFitToMap);

export const getMapType = createSelector(getLayoutState, (state: ILayoutState): EMapTypeId => state.mapType);

export const getReplayLayout = createSelector(getLayoutState, (state: ILayoutState): IReplayLayout => state.replayLayout);

export const getIsReplayOpen = createSelector(getReplayLayout, (state: IReplayLayout): boolean => state.isReplayOpen);

export const getIsReplayDatePickerPanelOpen = createSelector(
  getReplayLayout,
  (state: IReplayLayout): boolean => state.isReplayDatePickerOpen
);

export const getPanAndZoomToLocation = createSelector(getLayoutState, (state: ILayoutState): ILatLng => state.locationToPanAndZoom);

export const getIsManagePostedSpeedOpen = createSelector(getLayoutState, (state: ILayoutState): boolean => state.isManagePostedSpeedOpen);

export const getAnimationState = createSelector(getLayoutState, (state): EMapAnimationState => state.animationState);

export const getShowReplay = createSelector(
  getAnimationState,
  getIsReplayOpen,
  (animationState, isReplayOpen): boolean =>
    animationState === EMapAnimationState.replayPanelOpen || (isReplayOpen && animationState === EMapAnimationState.replayPanelClosed)
);

export const getShowManagePostedSpeed = createSelector(
  getIsManagePostedSpeedOpen,
  (isManagePostedSpeedOpen): boolean => isManagePostedSpeedOpen
);

export const getIsFullscreen = createSelector(getLayoutState, (state: ILayoutState): boolean => state.isFullscreen);

export const getIsAppAnimationsEnabled = createSelector(getLayoutState, (state: ILayoutState): boolean => state.isAppAnimationsEnabled);

export const getLayoutMarginsMap = createSelector(getLayoutState, (state): IIndexableObject<Position> => state.overlayMargins);

export const getLayoutMargins = createSelector(
  getIsReplayOpen,
  getIsManagePostedSpeedOpen,
  getLayoutMarginsMap,
  (isReplayOpen, isManagePostedSpeedOpen, marginsMap): Position => {
    let overlays: EOverlayContainer[];
    if (isReplayOpen) {
      overlays = [EOverlayContainer.Replay];
    } else if (isManagePostedSpeedOpen) {
      overlays = [EOverlayContainer.ManagePostedSpeed, EOverlayContainer.MapControls];
    } else {
      overlays = [EOverlayContainer.VehicleDriverPanel, EOverlayContainer.MapControls];
    }
    const layoutMargins: Position = aggregateMargins(overlays, marginsMap);

    return layoutMargins;
  }
);

function aggregateMargins(overlays: EOverlayContainer[], marginsMap: IIndexableObject<Position>): Position {
  const margins = new Position();
  overlays.forEach(overlay => {
    const currentMargins = marginsMap[overlay];
    if (!isElementNull(currentMargins)) {
      margins.top = Math.max(margins.top, currentMargins.top);
      margins.right = Math.max(margins.right, currentMargins.right);
      margins.bottom = Math.max(margins.bottom, currentMargins.bottom);
      margins.left = Math.max(margins.left, currentMargins.left);
    }
  });

  return margins;
}
